export type HreflangLink = {hrefLang: string; href: string};

// A localized version of the current page: site language code + path (with lang prefix).
export type HreflangAlternate = {lang: string; path: string};

type PrismicDocMeta = {lang: string; uid?: string | null};

// Prismic only has these locales; every other site language falls back to English content.
const PRISMIC_LANGUAGES = ['en', 'es', 'fr'];

const withTrailingSlash = (url: string) => (url.endsWith('/') ? url : url + '/');

export const localizePath = (lang: string, defaultLanguage: string, originalPath: string) =>
  withTrailingSlash((lang === defaultLanguage ? '' : '/' + lang) + originalPath);

// 'es-es' -> 'es'
const prismicLangToSiteLang = (prismicLang: string) => prismicLang.slice(0, 2).toLowerCase();

/**
 * Builds hreflang alternates from a Prismic document and its translations, so only
 * URLs that actually exist get an hreflang. `fallbackLanguages` are site languages
 * that render the English document under their own prefix (e.g. /ca/page/:uid/).
 */
export const getPrismicAlternates = ({
  basePath,
  defaultLanguage,
  current,
  alternateLanguages = [],
  fallbackLanguages = []
}: {
  basePath: string;
  defaultLanguage: string;
  current: PrismicDocMeta;
  alternateLanguages?: (PrismicDocMeta | null | undefined)[] | null;
  fallbackLanguages?: string[];
}): HreflangAlternate[] => {
  const uidByLang = new Map<string, string>();
  [current, ...(alternateLanguages || [])].forEach((doc) => {
    if (!doc?.uid) return;
    const lang = prismicLangToSiteLang(doc.lang);
    if (PRISMIC_LANGUAGES.includes(lang) && !uidByLang.has(lang)) uidByLang.set(lang, doc.uid);
  });

  const englishUid = uidByLang.get('en');
  if (englishUid) {
    fallbackLanguages
      .filter((lang) => !uidByLang.has(lang))
      .forEach((lang) => uidByLang.set(lang, englishUid));
  }

  return Array.from(uidByLang.entries()).map(([lang, uid]) => ({
    lang,
    path: localizePath(lang, defaultLanguage, `${basePath}/${uid}/`)
  }));
};

/**
 * Without `alternates`, every site language gets an hreflang at the same path.
 * x-default always points at the default (English) version, and is omitted when
 * no English version exists.
 */
export const getHreflangLinks = ({
  siteUrl,
  defaultLanguage,
  languages,
  originalPath,
  alternates
}: {
  siteUrl: string;
  defaultLanguage: string;
  languages: string[];
  originalPath: string;
  alternates?: HreflangAlternate[];
}): HreflangLink[] => {
  const resolved =
    alternates ||
    languages.map((lang) => ({lang, path: localizePath(lang, defaultLanguage, originalPath)}));

  const links = resolved.map(({lang, path}) => ({hrefLang: lang, href: siteUrl + path}));
  const defaultVersion = resolved.find(({lang}) => lang === defaultLanguage);
  if (defaultVersion) {
    links.push({hrefLang: 'x-default', href: siteUrl + defaultVersion.path});
  }
  return links;
};

type PrismicDocWithAlternates = PrismicDocMeta & {
  alternateLanguages?: (PrismicDocMeta | null | undefined)[] | null;
};

/**
 * Alternates for a page rendered from a Prismic template, read from its GraphQL `data`
 * (blog post or /page/:uid). Returns undefined for any other page, meaning every
 * language exists. Shared by the template's hreflang tags and the layout's language
 * selector, so both only point at translations that actually exist.
 */
export const getPrismicPageAlternates = ({
  data,
  defaultLanguage,
  languages
}: {
  data: any;
  defaultLanguage: string;
  languages: string[];
}): HreflangAlternate[] | undefined => {
  const postMeta: PrismicDocWithAlternates | undefined = data?.prismic?.post?._meta;
  if (postMeta) {
    return getPrismicAlternates({
      basePath: '/blog',
      defaultLanguage,
      current: postMeta,
      alternateLanguages: postMeta.alternateLanguages
    });
  }

  const pageMeta: PrismicDocWithAlternates | undefined =
    data?.prismic?.allPages?.edges?.[0]?.node?._meta;
  if (pageMeta) {
    return getPrismicAlternates({
      basePath: '/page',
      defaultLanguage,
      current: pageMeta,
      alternateLanguages: pageMeta.alternateLanguages,
      // gatsby-config clones English Prismic pages into these languages (/ca/page/:uid/ etc.)
      fallbackLanguages: languages.filter((lng) => !PRISMIC_LANGUAGES.includes(lng))
    });
  }

  return undefined;
};

/**
 * Entries for the language selector: each language with its path without the language
 * prefix (as `Link` from gatsby-plugin-react-i18next expects). With `alternates`, only
 * languages that have a version of the page are listed, so the selector never links to a 404.
 */
export const getLanguageSelectorLinks = ({
  languages,
  defaultLanguage,
  originalPath,
  alternates
}: {
  languages: string[];
  defaultLanguage: string;
  originalPath: string;
  alternates?: HreflangAlternate[];
}): {lang: string; to: string}[] => {
  if (!alternates) return languages.map((lang) => ({lang, to: originalPath}));
  const pathByLang = new Map(alternates.map(({lang, path}) => [lang, path]));
  return languages
    .filter((lang) => pathByLang.has(lang))
    .map((lang) => {
      const prefix = lang === defaultLanguage ? '' : '/' + lang;
      return {lang, to: (pathByLang.get(lang) as string).slice(prefix.length)};
    });
};
